import {useCallback, useMemo} from 'react';

import {Box} from '@mui/material';
import {useSearchParams} from 'react-router-dom';

import {PaymentLinkQR} from '@/app/main/paymentLink/components';
import BillingAddressForm from '@/app/main/paymentLink/v2/pages/paymentLinkV2/components/PaymentLinkV2Valid/components/BillingAddressForm/BillingAddressForm';
import {IPaymentLinkV2ValidProps} from '@/app/main/paymentLink/v2/pages/paymentLinkV2/models/interfaces/paymentLinkV2.interfaces';
import {
  CAMPAIGN_TYPE_LABELS,
  CampaignType,
  PaymentLinkV2Types,
} from '@/dataSource/src/api/payment-link/v2/types';

import '../../styles';
import './styles';

import {PAYMENT_LINK_V2_QUERY_PARAMS} from '../../models/constants/constants';
import PaymentLinkV2Footer from '../PaymentLinkV2Footer';
import PaymentLinkV2Header from '../PaymentLinkV2Header';

import {ApplePayFastCheckout} from './components/ApplePayFastCheckout';
import PaymentLinkV2LimitFeeBox from './components/PaymentLinkV2LimitFeeBox/PaymentLinkV2LimitFeeBox';
import {
  PaymentMethodV2,
  PaymentMethodV2Type,
} from './components/PaymentMethodsSelector/models/types';
import PaymentMethodsSelector from './components/PaymentMethodsSelector/PaymentMethodsSelector';
import SavingsGoalNote from './components/SavingsGoalNote';
import {useActions} from './useActions';

export const PaymentLinkV2Valid = (props: IPaymentLinkV2ValidProps) => {
  const {
    billingFormRef,
    billingValues,
    setBillingValues,
    submitBilling,
    handleEditAddressBackButton,
    disableButtonContinue,
    disableApplePayButton,
    paymentSessionCheckout,
    selectedAmount,
    setSelectedAmount,
    limitsFees,
    isLink,
    checkoutMaxAmount,
    setIsSelectTabAmount,
    setSelectedFee,
    PAYMENT_METHODS,
    selectedMethod,
    handleSelectMethod,
    applePayRef,
    googlePayRef,
    cardRef,
    selectedMethodFee,
    isSimplifiedBilling,
    goalNote,
    setGoalNote,
    applePayApiEnabled,
    isApplePayAvailable,
    startApplePaySession,
  } = useActions(props);

  const renderBillingForm = useCallback(
    (selectedId: PaymentMethodV2Type) => {
      // Native Apple Pay (flag ON): show the Apple Pay button instead of the
      // billing form — payer data comes from Apple Wallet. If the native session
      // isn't available we render nothing (the option is also hidden upstream).
      // Flag OFF → billing form + Web Components flow (unchanged).
      if (selectedId === PaymentMethodV2.APPLEPAY && applePayApiEnabled) {
        return isApplePayAvailable ? (
          <ApplePayFastCheckout
            onClick={startApplePaySession}
            disabled={disableApplePayButton}
          />
        ) : null;
      }

      return (
        <Box className="pt-[4px] px-[24px] pb-[24px]">
          <BillingAddressForm
            ref={billingFormRef}
            formValue={billingValues}
            startCollapsed={true}
            onChange={setBillingValues}
            onSubmit={submitBilling}
            onCollapsedChange={handleEditAddressBackButton}
            disableButtonContinue={disableButtonContinue}
            simplified={isSimplifiedBilling}
          />
        </Box>
      );
    },
    [
      applePayApiEnabled,
      isApplePayAvailable,
      startApplePaySession,
      billingFormRef,
      billingValues,
      setBillingValues,
      submitBilling,
      handleEditAddressBackButton,
      disableButtonContinue,
      disableApplePayButton,
      isSimplifiedBilling,
    ],
  );

  const [searchParams] = useSearchParams();
  const baseAmount = useMemo<number | null>(() => {
    const value = searchParams.get(PAYMENT_LINK_V2_QUERY_PARAMS.BASE_AMOUNT);
    if (value === null) return null;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, [searchParams]);

  const amountHeader = useMemo(() => {
    if (baseAmount !== null && baseAmount > 0) {
      return null;
    }
    if (props.amount > 0) {
      return props.amount;
    }
    return undefined;
  }, [props.amount, baseAmount]);

  const isCampaign = props.type === PaymentLinkV2Types.CAMPAIGN;
  const savingsGoal = props.savingsGoalConfiguration;
  const isSavingsGoal = props.type === PaymentLinkV2Types.SAVING_GOAL;
  const campaignImage = props.campaignConfiguration?.imageUrl;
  const campaignTypeKey = props.campaignConfiguration?.type;
  const campaignType =
    CAMPAIGN_TYPE_LABELS[campaignTypeKey?.toLowerCase() as CampaignType] ??
    campaignTypeKey;
  const headerName = isCampaign
    ? props.campaignConfiguration?.title
    : props.name;
  const headerNote = isCampaign
    ? props.campaignConfiguration?.description
    : isSavingsGoal
      ? savingsGoal?.motivation
      : props.note;

  return (
    <section className="payment-link-v2">
      <Box
        className={`payment-link-v2-container${
          props.gif || campaignImage
            ? ' payment-link-v2-container--with-gif'
            : ''
        }`}>
        <PaymentLinkV2Header
          paymentLinkId={props.paymentLinkId}
          name={headerName}
          gif={props.gif}
          image={campaignImage}
          fundraiserType={campaignType}
          note={headerNote}
          amount={amountHeader ?? undefined}
          isCampaign={isCampaign}
          isSavingsGoal={isSavingsGoal}
          goalName={savingsGoal?.name}
        />
        {!isLink && (
          <PaymentLinkQR
            maxAmount={checkoutMaxAmount}
            setIsSelectTabAmount={setIsSelectTabAmount}
            feesData={null}
            setSelectedFee={setSelectedFee}
            selectedAmount={selectedAmount}
            setSelectedAmount={setSelectedAmount}
          />
        )}
        {isSavingsGoal && (
          <SavingsGoalNote
            name={props.name}
            value={goalNote}
            onChange={setGoalNote}
          />
        )}
        <PaymentLinkV2LimitFeeBox
          receiverName={props.name}
          receiverAmount={selectedAmount}
          selectedMethod={selectedMethodFee}
          limitsFees={limitsFees}
        />
        <PaymentMethodsSelector
          options={PAYMENT_METHODS}
          selectedId={selectedMethod}
          onSelect={id => handleSelectMethod(id)}
          renderForm={renderBillingForm}>
          {paymentSessionCheckout && (
            <Box
              sx={{
                padding: 2,
              }}>
              <div
                ref={applePayRef}
                id="applepay-container"
              />
              <div
                ref={googlePayRef}
                id="googlepay-container"
              />
              <div
                ref={cardRef}
                id="flow-container"
              />
            </Box>
          )}
        </PaymentMethodsSelector>

        <PaymentLinkV2Footer isCampaign={isCampaign} />
      </Box>
    </section>
  );
};
